/**
 * useDisclaimerStatus - Cached disclaimer status hook
 *
 * Uses React Query to cache the disclaimer status across page navigations.
 * This prevents redundant API calls when navigating between compensation pages.
 *
 * @module hooks/compensation/useDisclaimerStatus
 */

import { useQuery } from '@tanstack/react-query';
import type {
  CheckDisclaimerStatusResponse,
} from '@/lib/types/compensation';
import { checkDisclaimerStatus as checkDisclaimerStatusApi } from '@/domain/compensation/apiRest';

/**
 * Base query key for disclaimer status (used for prefix invalidation)
 */
export const DISCLAIMER_STATUS_KEY = ['compensation', 'disclaimer-status'] as const;

/**
 * Fetch disclaimer status from the REST API
 */
async function fetchDisclaimerStatus(tenantId: string): Promise<CheckDisclaimerStatusResponse> {
  const result = await checkDisclaimerStatusApi(tenantId);
  return {
    acknowledged: result.acknowledged,
    acknowledgment: result.acknowledgment,
  };
}

/**
 * Hook to get cached disclaimer status
 *
 * Benefits:
 * - Caches the result per-tenant for 5 minutes (staleTime)
 * - Shares data across all components using this hook
 * - Prevents duplicate API calls when navigating between compensation pages
 * - Uses background refetch on window focus
 *
 * @param options.enabled - Whether to fetch (default: true)
 * @param options.tenantId - Tenant ID to scope the cache key and API call
 * @returns Query result with disclaimer status
 */
export function useDisclaimerStatus(options?: { enabled?: boolean; tenantId?: string }) {
  const tenantId = options?.tenantId ?? '';
  return useQuery({
    queryKey: [...DISCLAIMER_STATUS_KEY, tenantId],
    queryFn: () => fetchDisclaimerStatus(tenantId),
    enabled: (options?.enabled ?? true) && !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes - disclaimer status rarely changes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes (previously cacheTime)
    refetchOnWindowFocus: false, // Don't refetch on window focus - status is stable
    retry: 1, // Only retry once on failure
  });
}

/**
 * Invalidate disclaimer status cache
 * Call this after acknowledging the disclaimer
 */
export { DISCLAIMER_STATUS_KEY as disclaimerStatusQueryKey };
